import React, { useState, useEffect, useCallback, useRef } from "react";
import { Calendar, Users, MapPin, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { motion, AnimatePresence, useInView } from "framer-motion";

import eSummitPhoto2 from "./esummit/photo-2.JPG";    
import eSummitPhoto3 from "./esummit/photo-3.JPG";  
import eSummitPhoto4 from "./esummit/photo-4.JPG";   
import eSummitPhoto5 from "./esummit/photo-5.JPG";  
import eSummitPhoto6 from "./esummit/photo-6.JPG"; 
import eSummitPhoto7 from "./esummit/photo-7.JPG";
import eSummitPhoto8 from "./esummit/photo-8.JPG";
import eSummitPhoto10 from "./esummit/photo-10.JPG";
// ─── Past Events (archive) images – keep your existing imports ────────────────
import img10 from "../ProgramImages/5.png";
import img11 from "../ProgramImages/6.png";
import img12 from "../ProgramImages/7.png";
import img13 from "../ProgramImages/12.jpg";
import img14 from "../ProgramImages/13.jpg";
import img15 from "../ProgramImages/14.jpg";
import img16 from "../ProgramImages/15.jpg";
import img17 from "../ProgramImages/16.jpg";
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════
//  DATA
// ═══════════════════════════════════════════════════════════════════════
const E_SUMMIT = {
  title: "E-SUMMIT '26",
  subtitle: "Pioneering the Impossible",
  description:
    "Our flagship event concluded with resounding success — bringing together thousands of students, visionary entrepreneurs, top-tier investors, and industry experts. Two days of intense pitch battles, deep-tech showcases, and invaluable networking.",
  venue: "IIT Ropar Campus",
  date: "11 – 12 April 2026",
  footfall: "2,000+ Attendees",
  photos: [
   
    eSummitPhoto2,
    eSummitPhoto4,
    eSummitPhoto5,
    eSummitPhoto7,
    eSummitPhoto8,
    eSummitPhoto10
    
  ],
};

const PAST_EVENTS = [
  { img: img10, title: "Startup Pitch Day",      tag: "Funding",    description: "Seed-stage pitching with live VC feedback." },
  { img: img11, title: "Founders Meetup",        tag: "Network",    description: "Candid conversations with IIT alumni founders." },
  { img: img12, title: "Deep-Tech Symposium",    tag: "Research",   description: "Lab-to-market stories from breakthrough scientists." },
  { img: img13, title: "Mentorship Clinic",      tag: "Growth",     description: "One-on-one sessions with industry stalwarts." },
  { img: img14, title: "Investor Connect",       tag: "Deals",      description: "Exclusive VC dinner and warm intros." },
  { img: img15, title: "Hackathon Finale",       tag: "Build",      description: "36-hour marathon to ship, not just hack." },
  { img: img16, title: "Defence Tech Expo",      tag: "Gov-Tech",   description: "Showcasing innovations directly to the Armed Forces." },
  { img: img17, title: "Women in Tech",          tag: "Community",  description: "Celebrating and amplifying female founders." },
];

function StatPill({ icon: Icon, label, color }) {
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border bg-white shadow-sm
                     hover:shadow-md transition-shadow duration-200 cursor-default select-none
                     ${color}`}>
      <span className="flex-shrink-0 p-1.5 rounded-lg bg-current/10">
        <Icon size={15} className="opacity-80" />
      </span>
      <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
    </div>
  );
}

function SliderArrow({ direction, onClick }) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      className="flex items-center justify-center w-11 h-11 rounded-full 
                 bg-white/80 backdrop-blur-md border border-slate-200
                 text-slate-800 shadow-sm hover:bg-white hover:text-blue-600 hover:shadow-md
                 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-blue-400"
      aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
    >
      <Icon size={20} strokeWidth={2.5} />
    </motion.button>
  );
}

const SLIDE_VARIANTS = {
  enter:  (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit:   (d) => ({ x: d < 0 ? "-100%" : "100%", opacity: 0, transition: { duration: 0.45, ease: [0.55, 0, 1, 0.45] } }),
};

function ImageSlider({ photos }) {
  const [[idx, dir], setPage] = useState([0, 0]);
  const [paused, setPaused] = useState(false);
  const dragStartX = useRef(null);

  const paginate = useCallback((newDir) => {
    setPage(([i]) => {
      const next = newDir > 0
        ? (i === photos.length - 1 ? 0 : i + 1)
        : (i === 0 ? photos.length - 1 : i - 1);
      return [next, newDir];
    });
  }, [photos.length]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => paginate(1), 4200);
    return () => clearInterval(t);
  }, [paused, paginate]);

  const goTo = (i) => setPage(([cur]) => [i, i > cur ? 1 : -1]);

  const onDragStart = (e) => { 
    dragStartX.current = e.clientX || (e.touches && e.touches[0].clientX); 
  };
  const onDragEnd   = (e) => {
    if (dragStartX.current === null) return;
    const endX  = e.clientX || (e.changedTouches && e.changedTouches[0].clientX);
    const delta = (dragStartX.current - endX);
    if (Math.abs(delta) > 40) paginate(delta > 0 ? 1 : -1);
    dragStartX.current = null;
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-white select-none rounded-r-none lg:rounded-r-[2rem]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onMouseDown={onDragStart}
      onMouseUp={onDragEnd}
      onTouchStart={onDragStart}
      onTouchEnd={onDragEnd}
    >
      <AnimatePresence initial={false} custom={dir}>
        {/* We use a flex wrapper and natural scaling so the rounded corners 
            perfectly "hug" the visual image instead of leaving empty space */}
        <motion.div
          key={`fg-${idx}`}
          custom={dir}
          variants={SLIDE_VARIANTS}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0 z-10 flex items-center justify-center p-4 lg:p-8"
        >
          <img
            src={photos[idx]}
            className="max-w-full max-h-full w-auto h-auto rounded-2xl shadow-xl border border-slate-200/60"
            draggable={false}
            alt={`Highlight ${idx + 1}`}
          />
        </motion.div>
      </AnimatePresence>

      {/* Scrim – subtle light gradient to maintain spacing context at the bottom */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent pointer-events-none z-20" />

      {/* Control Bar (Dots + Arrows) - Kept out of the way of faces */}
      <div className="absolute bottom-0 inset-x-0 p-6 z-30 flex items-center justify-between pointer-events-none">
        
        {/* Dots (Aligned left) */}
        <div className="flex items-center gap-2 pointer-events-auto pl-2">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 focus-visible:outline-none shadow-sm
                          ${i === idx ? "w-8 h-2 bg-blue-600" : "w-2 h-2 bg-slate-300 hover:bg-slate-400"}`}
            />
          ))}
        </div>

        {/* Arrows (Aligned right) */}
        <div className="flex items-center gap-3 pointer-events-auto pr-2">
          <SliderArrow direction="prev" onClick={() => paginate(-1)} />
          <SliderArrow direction="next" onClick={() => paginate(1)} />
        </div>

      </div>

      {/* Progress bar */}
      {!paused && (
        <motion.div
          key={`progress-${idx}`}
          className="absolute bottom-0 left-0 h-[4px] bg-blue-500 z-30"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 4.2, ease: "linear" }}
        />
      )}
    </div>
  );
}

function ArchiveCard({ event, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08, ease: "easeOut" }}
      className="group relative overflow-hidden rounded-3xl bg-slate-100
                 border border-slate-200/80 shadow-sm aspect-[4/3]
                 hover:shadow-xl hover:shadow-blue-900/10 hover:-translate-y-1.5
                 transition-all duration-300 cursor-pointer block"
    >
      <img
        src={event.img}
        alt={event.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 z-0"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-300 z-10" />

      <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-gradient-to-r from-blue-500 to-cyan-400
                      scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left z-20" />
    </motion.article>
  );
}

function SectionHeader({ eyebrow, title, accent, subtitle }) {
  return (
    <div className="mb-12">
      {eyebrow && (
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-500 mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
        {title}{" "}
        {accent && <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">{accent}</span>}
      </h2>
      {subtitle && <p className="text-slate-500 text-base max-w-xl leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
export default function App() {
  return (
    <div className="bg-[#f8f9fb] min-h-screen font-sans text-slate-900 overflow-x-hidden">

      {/* ── Hero Header ─────────────────────────────────────────── */}
      <header className="relative pt-32 pb-20 px-6 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)]
                        [background-size:22px_22px] opacity-50 pointer-events-none" />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                        w-[700px] h-[320px] bg-blue-400/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/4 right-1/4 w-[200px] h-[200px]
                        bg-cyan-400/8 rounded-full blur-[80px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 inline-flex items-center gap-2 px-4 py-1.5 mb-6
                     rounded-full bg-white border border-blue-100 shadow-sm
                     text-blue-600 text-xs font-bold uppercase tracking-widest"
        >
          <Zap size={12} className="fill-blue-500" />
          Community & Culture
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative z-10 text-5xl md:text-6xl lg:text-7xl font-black tracking-tight
                     text-slate-900 leading-[1.05] mb-5"
        >
          Where Ideas{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
            Come Alive
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-10 text-lg text-slate-500 max-w-xl mx-auto leading-relaxed"
        >
          Flagship summits, deep-tech forums, and community gatherings that shape the next generation of founders.
        </motion.p>
      </header>

      {/* ── E-Summit Highlight ──────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-28">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.25 }}
          className="relative bg-white rounded-[2rem] overflow-hidden
                     border border-slate-200/80 shadow-2xl shadow-blue-900/6 flex flex-col lg:flex-row"
        >
          <div className="absolute top-0 inset-x-0 h-[3px]
                          bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-600 z-10" />

          {/* Left panel – info */}
          <div className="lg:w-[48%] p-8 md:p-12 flex flex-col justify-center">

            <span className="inline-flex items-center gap-2 w-max px-3.5 py-1.5 mb-7
                             rounded-full bg-amber-50 border border-amber-200
                             text-amber-600 text-[11px] font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Recent Highlight
            </span>

            <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-none mb-2">
              {E_SUMMIT.title}
            </h2>
            <p className="text-xl font-bold text-transparent bg-clip-text
                          bg-gradient-to-r from-blue-600 to-cyan-600 mb-6">
              {E_SUMMIT.subtitle}
            </p>

            <p className="text-slate-500 text-[15px] leading-[1.75] mb-9 max-w-prose">
              {E_SUMMIT.description}
            </p>

            <div className="flex flex-wrap gap-3">
              <StatPill icon={Calendar} label={E_SUMMIT.date}    color="text-blue-600 border-blue-100" />
              <StatPill icon={Users}    label={E_SUMMIT.footfall} color="text-emerald-600 border-emerald-100" />
              <StatPill icon={MapPin}   label={E_SUMMIT.venue}   color="text-violet-600 border-violet-100" />
            </div>
          </div>

          {/* Right panel – slider */}
          <div className="lg:w-[52%] relative h-[380px] lg:h-[560px] bg-white">
            <ImageSlider photos={E_SUMMIT.photos} />
          </div>
        </motion.div>
      </section>

      {/* ── Event Archive ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-24">

        <div className="flex items-center gap-4 mb-12">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 px-2">
            Archive
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <SectionHeader
          eyebrow="Past Events"
          title="Building the Future"
          accent="Together"
          subtitle="Every gathering is a chapter in our story — fostering connections, sparking ideas, and celebrating the builder's spirit."
        />

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 items-stretch">
          {PAST_EVENTS.map((event, i) => (
            <ArchiveCard key={event.title} event={event} index={i} />
          ))}
        </div>
      </section>

    </div>
  );
}